import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { createDocumentFromTemplate } from "../utility/api";

// Async thunk for creating document
export const createDocumentAsync = createAsyncThunk(
	"assignment/createDocument",
	async ({ templateId, documentName, metadata }, { rejectWithValue }) => {
		try {
			const response = await createDocumentFromTemplate(
				templateId,
				documentName,
				metadata,
			);
			return response;
		} catch (error) {
			return rejectWithValue(error.message);
		}
	},
);

// Async thunk for updating assignment
export const updateAssignmentAsync = createAsyncThunk(
	"assignment/updateAssignment",
	async ({ assignmentId, documentId }, { rejectWithValue, getState }) => {
		try {
			const state = getState();
			const assignment = state.assignment.assignment;
			const user = state.user.user;

			if (!assignment) {
				throw new Error("No assignment found in state");
			}

			// Update the assignment with new document ID
			const updatedAssignment = {
				...assignment,
				openSignDocumentId: documentId,
			};

			// Call the API to update the assignment
			const { updateAssignment } = await import("../utility/api");
			await updateAssignment(updatedAssignment, user);

			return { assignmentId, documentId };
		} catch (error) {
			return rejectWithValue(error.message);
		}
	},
);

const AssignmentSlice = createSlice({
	name: "assignment",
	initialState: {
		assignment: null,
		loading: false,
		error: null,
		documentCreation: {
			loading: false,
			error: null,
		},
	},
	reducers: {
		setAssignment: (state, action) => {
			state.assignment = action.payload;
			state.error = null;
		},
		clearAssignment: (state) => {
			state.assignment = null;
			state.error = null;
		},
		clearErrors: (state) => {
			state.error = null;
			if (state.documentCreation) {
				state.documentCreation.error = null;
			} else {
				state.documentCreation = {
					loading: false,
					error: null,
				};
			}
		},
		resetDocumentCreation: (state) => {
			state.documentCreation = {
				loading: false,
				error: null,
			};
		},
	},
	extraReducers: (builder) => {
		builder
			// Handle createDocumentAsync
			.addCase(createDocumentAsync.pending, (state) => {
				state.documentCreation.loading = true;
				state.documentCreation.error = null;
			})
			.addCase(createDocumentAsync.fulfilled, (state, action) => {
				state.documentCreation.loading = false;
				if (state.assignment) {
					// Directly update the assignment in state with the new document ID
					state.assignment.openSignDocumentId =
						action.payload.documentId;
				}
			})
			.addCase(createDocumentAsync.rejected, (state, action) => {
				state.documentCreation.loading = false;
				state.documentCreation.error = action.payload;
			})
			// Handle updateAssignmentAsync
			.addCase(updateAssignmentAsync.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateAssignmentAsync.fulfilled, (state, action) => {
				state.loading = false;
				if (state.assignment) {
					state.assignment.openSignDocumentId =
						action.payload.documentId;
				}
			})
			.addCase(updateAssignmentAsync.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});
	},
});

export const {
	setAssignment,
	clearAssignment,
	clearErrors,
	resetDocumentCreation,
} = AssignmentSlice.actions;
export default AssignmentSlice.reducer;
